import { create } from 'zustand'
import { ref as dbRef, onValue, set as dbSet, update, type Unsubscribe } from 'firebase/database'
import { collection, doc, getDocs, addDoc, deleteDoc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { rtdb, db } from '../firebase'
import { getErrorMessage } from '../types/firebase'

export interface LocationData {
  lat: number
  lng: number
  timestamp: string
  active: boolean
  sharedByUid: string
}

export interface SafeZone {
  id: string
  name: string
  lat: number
  lng: number
  radius: number
}

function haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

interface LocationState {
  sharing: boolean
  sharingProfileId: string | null
  watchId: number | null
  trackedLocation: LocationData | null
  lastSeenMap: Record<string, LocationData>
  safeZones: SafeZone[]
  loadingZones: boolean
  _trackedUnsub: Unsubscribe | null
  _cardUnsubs: Map<string, Unsubscribe>

  startSharing: (profileId: string, uid: string) => Promise<void>
  stopSharing: (profileId: string) => Promise<void>
  writeLocation: (profileId: string, lat: number, lng: number, uid: string) => void
  listenToProfile: (profileId: string) => void
  stopListening: () => void
  listenToAllProfiles: (profileIds: string[]) => void
  stopListeningToAllProfiles: () => void
  fetchSafeZones: (uid: string) => Promise<void>
  addSafeZone: (zone: Omit<SafeZone, 'id'>, uid: string) => Promise<void>
  deleteSafeZone: (id: string, uid: string) => Promise<void>
  updateSafeZone: (id: string, updates: Partial<Omit<SafeZone, 'id'>>, uid: string) => Promise<void>
  isInSafeZone: (lat: number, lng: number) => SafeZone | null
  isStale: (data: LocationData) => boolean
  cleanup: () => void
}

export const useLocationStore = create<LocationState>()((set, get) => ({
  sharing: false,
  sharingProfileId: null,
  watchId: null,
  trackedLocation: null,
  lastSeenMap: {},
  safeZones: [],
  loadingZones: false,
  _trackedUnsub: null,
  _cardUnsubs: new Map(),

  async startSharing(profileId, uid) {
    if (!navigator.geolocation) throw new Error('Geolocation not supported')
    await new Promise<void>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          set({ sharing: true, sharingProfileId: profileId })
          get().writeLocation(profileId, pos.coords.latitude, pos.coords.longitude, uid)
          resolve()
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) reject(new Error('Location permission denied. Please allow location access in your browser settings.'))
          else reject(new Error('Could not get your location. Check your connection and try again.'))
        },
        { enableHighAccuracy: true, timeout: 10000 },
      )
    })
    const watchId = navigator.geolocation.watchPosition(
      (pos) => get().writeLocation(profileId, pos.coords.latitude, pos.coords.longitude, uid),
      (err) => console.warn('[location] watchPosition error:', err.message),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 },
    )
    set({ watchId })
  },

  async stopSharing(profileId) {
    const { watchId } = get()
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId)
      set({ watchId: null })
    }
    set({ sharing: false, sharingProfileId: null })
    await update(dbRef(rtdb, `location_sharing/${profileId}`), {
      active: false,
      timestamp: new Date().toISOString(),
    })
  },

  writeLocation(profileId, lat, lng, uid) {
    const data: LocationData = {
      lat, lng,
      timestamp: new Date().toISOString(),
      active: true,
      sharedByUid: uid,
    }
    dbSet(dbRef(rtdb, `location_sharing/${profileId}`), data).catch((err: unknown) => {
      console.warn('[location] writeLocation failed:', getErrorMessage(err))
    })
  },

  listenToProfile(profileId) {
    get()._trackedUnsub?.()
    // Pre-populate from cache
    const cached = get().lastSeenMap[profileId] ?? null
    set({ trackedLocation: cached })
    const unsub = onValue(dbRef(rtdb, `location_sharing/${profileId}`), (snap) => {
      const data = snap.exists() ? (snap.val() as LocationData) : null
      set((s) => ({
        trackedLocation: data,
        lastSeenMap: data
          ? { ...s.lastSeenMap, [profileId]: data }
          : Object.fromEntries(Object.entries(s.lastSeenMap).filter(([k]) => k !== profileId)),
      }))
    })
    set({ _trackedUnsub: unsub })
  },

  stopListening() {
    get()._trackedUnsub?.()
    set({ _trackedUnsub: null, trackedLocation: null })
  },

  listenToAllProfiles(profileIds) {
    const { _cardUnsubs } = get()
    for (const [id, unsub] of _cardUnsubs) {
      if (!profileIds.includes(id)) { unsub(); _cardUnsubs.delete(id) }
    }
    for (const id of profileIds) {
      if (_cardUnsubs.has(id)) continue
      const unsub = onValue(dbRef(rtdb, `location_sharing/${id}`), (snap) => {
        set((s) => ({
          lastSeenMap: snap.exists()
            ? { ...s.lastSeenMap, [id]: snap.val() as LocationData }
            : Object.fromEntries(Object.entries(s.lastSeenMap).filter(([k]) => k !== id)),
        }))
      })
      _cardUnsubs.set(id, unsub)
    }
  },

  stopListeningToAllProfiles() {
    const { _cardUnsubs } = get()
    for (const unsub of _cardUnsubs.values()) unsub()
    _cardUnsubs.clear()
  },

  async fetchSafeZones(uid) {
    set({ loadingZones: true })
    try {
      const snap = await getDocs(collection(db, 'users', uid, 'safeZones'))
      set({ safeZones: snap.docs.map(d => ({ id: d.id, ...d.data() } as SafeZone)) })
    } finally {
      set({ loadingZones: false })
    }
  },

  async addSafeZone(zone, uid) {
    const ref = await addDoc(collection(db, 'users', uid, 'safeZones'), { ...zone, createdAt: serverTimestamp() })
    set((s) => ({ safeZones: [...s.safeZones, { id: ref.id, ...zone }] }))
  },

  async deleteSafeZone(id, uid) {
    await deleteDoc(doc(db, 'users', uid, 'safeZones', id))
    set((s) => ({ safeZones: s.safeZones.filter(z => z.id !== id) }))
  },

  async updateSafeZone(id, updates, uid) {
    await updateDoc(doc(db, 'users', uid, 'safeZones', id), updates)
    set((s) => ({ safeZones: s.safeZones.map(z => z.id === id ? { ...z, ...updates } : z) }))
  },

  isInSafeZone(lat, lng) {
    for (const zone of get().safeZones) {
      if (haversine(lat, lng, zone.lat, zone.lng) <= zone.radius) return zone
    }
    return null
  },

  isStale(data) {
    if (!data.active) return false
    return Date.now() - new Date(data.timestamp).getTime() > 5 * 60 * 1000
  },

  cleanup() {
    const { watchId } = get()
    if (watchId !== null) navigator.geolocation.clearWatch(watchId)
    get().stopListening()
    get().stopListeningToAllProfiles()
    set({ sharing: false, sharingProfileId: null, watchId: null })
  },
}))
